import { useState } from 'react'
import { signInWithGoogle, signUpWithEmail, signInWithEmail, type User } from '../../lib/auth'

interface SignInFormProps {
  onSignedIn: (user: User) => void
}

export default function SignInForm({ onSignedIn }: SignInFormProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleGoogle() {
    setError('')
    setLoading(true)
    try {
      const result = await signInWithGoogle()
      onSignedIn(result.user)
    } catch (err: any) {
      setError(err.message || 'Google sign-in did not go through. Try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signup') {
        const result = await signUpWithEmail(email, password)
        onSignedIn(result.user)
      } else {
        const result = await signInWithEmail(email, password)
        onSignedIn(result.user)
      }
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('That email already has an account. Try signing in instead.')
      } else if (err.code === 'auth/invalid-credential') {
        setError('That email and password do not match an account.')
      } else if (err.code === 'auth/weak-password') {
        setError('Use at least 6 characters for the password.')
      } else {
        setError(err.message || 'Sign-in did not go through. Try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-navy/75">
        Sign in to create a session. Teammates who join do not need an account.
      </p>

      <button
        onClick={handleGoogle}
        disabled={loading}
        className="btn w-full"
      >
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Sign in with Google
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-navy/25" />
        <span className="mono-label text-navy/70">or</span>
        <div className="flex-1 h-px bg-navy/25" />
      </div>

      <form onSubmit={handleEmailSubmit} className="space-y-3">
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-sm bg-paper outline-none"
        />
        <input
          required
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          minLength={6}
          className="w-full border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-sm bg-paper outline-none"
        />
        {error && <p className="text-brick text-sm border-l-2 border-brick pl-3">{error}</p>}
        <button type="submit" disabled={loading} className="btn btn-amber w-full">
          {loading ? 'One moment' : mode === 'signup' ? 'Create Account' : 'Sign In'}
        </button>
      </form>

      <p className="text-sm text-navy/70">
        {mode === 'signin' ? (
          <>
            No account?{' '}
            <button onClick={() => { setMode('signup'); setError('') }} className="text-amber font-semibold underline underline-offset-2 cursor-pointer">
              Sign up
            </button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <button onClick={() => { setMode('signin'); setError('') }} className="text-amber font-semibold underline underline-offset-2 cursor-pointer">
              Sign in
            </button>
          </>
        )}
      </p>
    </div>
  )
}
